import { Box, ThemeIcon, Tooltip, Text } from "@mantine/core";
import { IconPhoto, IconFileText, IconAlertCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import ProfileCollapsibleSection from "@/components/shared/ProfileCollapsibleSection";
import ImagesTabContent from "@/components/profile/ImagesTabContent";
import DocumentsTab from "@/components/shared/AnonymousCompanyProfile/DocumentsTab";
import DocumentsTabContent from "@/components/profile/DocumentsTabContent";
import { PROFILE_SECTION_HEADER_ICON_SIZE } from "@/shared/styles/profileSectionHeader";

type EditDocumentsProps = {
  additionalDocs: any[];
  existingDocs: any[];
  onAddAdditionalDoc: () => void;
  onUpdateAdditionalDoc: (index: number, field: string, value: any) => void;
  onRemoveAdditionalDoc: (index: number) => void;
  onDeleteExistingDoc: (docId: string) => void;
};

export type ProfileImagesDocumentsSectionsProps = {
  company: any;
  mode: "view" | "edit";
  anonymousView?: boolean;
  isMatchedView?: boolean;
  refreshCompany?: () => Promise<void>;
  activeTab?: string | null;
  aiImages?: string[];
  removeAiImage?: (index: number) => void;
  imagesSectionId?: string;
  documentsSectionId?: string;
  imagesValidationErrors?: string[];
  documentsValidationErrors?: string[];
  editDocuments?: EditDocumentsProps;
};

function ValidationHeaderHint({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;

  return (
    <Tooltip
      label={
        <Box>
          <Text fz="xs" fw={700} mb={4} c="white">
            Missing required fields:
          </Text>
          {errors.map((field) => (
            <Text key={field} fz="xs" c="gray.3">
              • {field}
            </Text>
          ))}
        </Box>
      }
      position="top"
      withArrow
      multiline
      color="dark.8"
    >
      <ThemeIcon size="xs" color="red" variant="transparent" style={{ cursor: "pointer" }}>
        <IconAlertCircle size={20} />
      </ThemeIcon>
    </Tooltip>
  );
}

export default function ProfileImagesDocumentsSections({
  company,
  mode,
  anonymousView = false,
  isMatchedView = false,
  refreshCompany = async () => {},
  activeTab = null,
  aiImages = [],
  removeAiImage,
  imagesSectionId = "images-section",
  documentsSectionId = "documents-section",
  imagesValidationErrors = [],
  documentsValidationErrors = [],
  editDocuments,
}: ProfileImagesDocumentsSectionsProps) {
  const iconSize = PROFILE_SECTION_HEADER_ICON_SIZE;

  const imagesHeaderRight: ReactNode = (
    <ValidationHeaderHint errors={imagesValidationErrors} />
  );
  const documentsHeaderRight: ReactNode = (
    <ValidationHeaderHint errors={documentsValidationErrors} />
  );

  return (
    <>
      <ProfileCollapsibleSection
        title="Images"
        icon={<IconPhoto size={iconSize} color="#ffc300" stroke={1.5} />}
        headerRight={imagesHeaderRight}
      >
        <Box id={imagesSectionId}>
          <ImagesTabContent
            company={company}
            refreshCompany={refreshCompany}
            isViewMode={mode === "view"}
            anonymousView={anonymousView}
            activeTab={activeTab}
            aiImages={aiImages}
            removeAiImage={removeAiImage}
            embedded
          />
        </Box>
      </ProfileCollapsibleSection>

      <ProfileCollapsibleSection
        title="Documents"
        icon={<IconFileText size={iconSize} color="#ffc300" stroke={1.5} />}
        headerRight={documentsHeaderRight}
      >
        <Box id={documentsSectionId}>
          {mode === "edit" && editDocuments ? (
            <DocumentsTabContent isViewMode={false} {...editDocuments} />
          ) : (
            <DocumentsTab company={company} isMatchedView={isMatchedView} embedded />
          )}
        </Box>
      </ProfileCollapsibleSection>
    </>
  );
}
